import { IML } from '../deps/iml.mjs';
import Features from '../features.mjs';
import { domToAST, SPACER } from '../helpers/iml.mjs';
import { create, is, getPackageIconSource } from '../utils.mjs';
import { createAiBlock, isAiBlock } from './aiBlock.mjs';
import FormanConfig from '../formanConfig.mjs';

/**
 * Accepts raw caret position and translates it to the position on text node. Sometimes raw caret position may point to a HTML element.
 *
 * @param node
 * @param offset
 * @param forward
 * @returns {object} Object with `node` and `offset` properties. Node is always a text node.
 */

export function resolveCaretPositionInCoder(node, offset, forward = false) {
	if (node.nodeName === 'IMT-CODER' || isAiBlock(node)) {
		if (offset === 0 || !node.firstChild) {
			// If offset is 0 or coder is empty
			if (node.firstChild && node.firstChild.nodeType !== 3) {
				// First child is an extra line, remove it
				if (node.firstChild.nodeType === 1 && node.firstChild.nodeName === 'BR') {
					node.firstChild.remove();
				}

				// First child is not a text field, build a SPACER there
				node.insertBefore(document.createTextNode(SPACER), node.firstChild);
			} else if (!node.firstChild) {
				// Empty coder
				node.appendChild(document.createTextNode(''));
			}
			// Set the first child to be an anchor
			node = node.firstChild;
		} else {
			if (!node.childNodes[offset]) {
				// Sometimes if happens in Chrome that offset points to undefined position in childNodes
				node = node.firstChild;
			} else {
				node = node.childNodes[offset];
			}
		}
	}

	if (node.nodeType === 3) {
		if (node.parentNode?.nodeName === 'IMT-CODER-TOKEN') {
			return { node: node.parentNode, offset };
		}

		// Ignore the span element, which is by default inserted by the browser when deleting and having the caret at the end
		if (node.parentNode?.nodeName === 'SPAN') {
			return { node: node.parentNode?.parentNode, offset };
		}

		// Text node - make sure we're a direct child of a coder
		if (!node.parentNode || (node.parentNode.nodeName !== 'IMT-CODER' && !isAiBlock(node.parentNode))) {
			return { node: null, offset: 0 };
		}
	}

	if (node.nodeType !== 3) {
		// Traverse up the dom to find imt-coder
		while (node) {
			if (node.parentNode && (node.parentNode.nodeName === 'IMT-CODER' || isAiBlock(node.parentNode))) {
				break;
			}
			node = node.parentNode;
		}

		// Break when we find orphaned node
		if (!node) return { node: null, offset: 0 };

		if (forward) {
			// Search first text sibling to the left
			if (node.nextSibling && node.nextSibling.nodeType === 3) {
				// Next is text, good for us
				node = node.nextSibling;
				offset = 0;
			} else {
				// No next or next isnt text, create SPACER
				const spacer = document.createTextNode(SPACER);

				if (node.nextSibling) {
					node.parentNode.insertBefore(spacer, node.nextSibling);
				} else {
					node.parentNode.appendChild(spacer);
				}
				node = spacer;
			}
		} else {
			const originalNode = node;

			// Search first text sibling to the left
			if (node.previousSibling && node.previousSibling.nodeType === 3) {
				// Prev is text, good for us
				node = node.previousSibling;
				offset = node.textContent.length;
			} else {
				// No prev or prev isnt text, create SPACER
				const spacer = document.createTextNode(SPACER);

				node.parentNode.insertBefore(spacer, node);
				node = spacer;
			}

			// Original node is an extra line, remove it
			if (originalNode.nodeType === 1 && originalNode.nodeName === 'BR') {
				originalNode.remove();
			}
		}
	}

	return {
		node,
		offset,
	};
}

export function getRange(includeAiBlock = false) {
	const sel = document.getSelection();

	if (sel.isCollapsed || !sel.rangeCount) return [];

	const range = sel.getRangeAt(0);
	const start = resolveCaretPositionInCoder(range.startContainer, range.startOffset);

	if (!start.node) return [];
	const end = resolveCaretPositionInCoder(range.endContainer, range.endOffset, true);

	// Start & end on the same node
	if (start.node === end.node) {
		// Selection covers the whole node or just a part of the text node
		const node =
			start.offset === 0 && end.offset === end.node.textContent.length
				? start.node
				: document.createTextNode(start.node.textContent.substring(start.offset, end.offset));

		// If the node is inside an AI block, we need to wrap it in a one
		return [includeAiBlock && isAiBlock(start.node.parentNode) ? createAiBlock([node]) : node];
	}

	const nodes = [];
	let node = start.node;

	if (start.offset === 0) {
		// Push the whole node
		nodes.push(node);
	} else {
		// Push just a part of the text node
		nodes.push(document.createTextNode(node.textContent.substr(start.offset)));
	}

	let aiBlockStartIndex = 0;

	do {
		// Jump out of an AI block
		if (includeAiBlock && !node.nextSibling && isAiBlock(node.parentNode)) {
			const aiBlock = createAiBlock(nodes.splice(aiBlockStartIndex));
			aiBlockStartIndex = nodes.push(aiBlock);
			node = node.parentNode;
		}

		node = node.nextSibling;

		// Jump into an AI block
		if (includeAiBlock && isAiBlock(node) && node.firstChild) {
			aiBlockStartIndex = nodes.length;
			node = node.firstChild;
		}

		if (!node) break;

		// Found end of the selection
		if (node === end.node) {
			// Push the whole node or just a part of the text node
			nodes.push(
				end.offset === end.node.textContent.length
					? end.node
					: document.createTextNode(end.node.textContent.substr(0, end.offset)),
			);

			// If the node is inside an AI block, we need to wrap it in a one
			if (includeAiBlock && isAiBlock(end.node.parentNode)) {
				const aiBlock = createAiBlock(nodes.splice(aiBlockStartIndex));
				nodes.push(aiBlock);
			}

			return nodes;
		}

		nodes.push(node);
	} while (node);

	// This should never occur - that means we didn't reach end node in siblings.
	return nodes;
}

export function copyImlFromRange(range) {
	const ast = domToAST(range, true);
	return IML.stringify(ast, undefined, { keepUnwantedOperators: true });
}

export function getCaret() {
	const sel = document.getSelection();

	if (!sel.anchorNode) return { node: null, offset: 0, collapsed: true };

	const { node, offset } = resolveCaretPositionInCoder(sel.anchorNode, sel.anchorOffset);
	if (!node) return { node: null, offset: 0, collapsed: true };

	return {
		node,
		offset,
		collapsed: sel.isCollapsed,
	};
}

/**
 * @param {ChildNode} node
 * @param {number} offset
 * @param {boolean} force
 */
export function setCaret(node, offset, force = false) {
	offset = Math.max(offset, 0);

	if (isCoderContainer(node)) {
		node = node.childNodes[0];
	}

	if (node && node.nodeType === 3) {
		offset = Math.min(node.textContent.length, offset);

		// Make sure we always set offset to 1 on SPACERs
		if (isSpacer(node) && !force) offset = 1;
	}

	const selection = window.getSelection();
	const range = document.createRange();

	range.setStart(node, offset);
	range.collapse(true);
	selection.removeAllRanges();
	selection.addRange(range);
}

export function isSpacer(elm) {
	if (!elm || elm.nodeType !== 3) return false;
	return elm.textContent === SPACER;
}

export function isDoubleSpacer(elm) {
	if (!elm || elm.nodeType !== 3) return false;
	return elm.textContent === SPACER + SPACER;
}

/**
 * Injects text node with an element.
 *
 * @param node Text node to inject.
 * @param {number} offset Offset.
 * @param elm Element to inject.
 */

export function injectTextNode(node, offset, elm) {
	if (!node || node.nodeType !== 3) return;

	if (offset === node.textContent.length) {
		// Inject end of the text node
		if (elm.nodeType === 3) {
			// With text node
			node.textContent += elm.textContent;
			setCaret(node, offset + elm.textContent.length);
		} else {
			// With HTML element
			if (node.nextSibling) {
				if (node.nextSibling.nodeType !== 3) {
					// Next sibling is HTML element
					node.parentNode.insertBefore(document.createTextNode(SPACER), node.nextSibling);
				}
				setCaret(node.nextSibling, 0);
				node.parentNode.insertBefore(elm, node.nextSibling);
			} else {
				// No sibling, append to the end
				node.parentNode.appendChild(elm);
				// Create SPACER
				const spacer = document.createTextNode(SPACER);

				node.parentNode.appendChild(spacer);
				setCaret(spacer, 0);
			}
		}
	} else if (offset === 0) {
		// Inject start of the text node
		if (elm.nodeType === 3) {
			// With text node
			node.textContent = elm.textContent + node.textContent;
			setCaret(node, elm.textContent.length);
		} else if (is(elm, 'imt-pill.function')) {
			node.parentNode.insertBefore(elm, node);
			setCaret(node, 0);
		} else {
			// With HTML element
			node.parentNode.insertBefore(elm, node);
			setCaret(node, elm.textContent.length);
		}
	} else {
		// Split the text node
		if (elm.nodeType === 3) {
			// With text node
			node.textContent = node.textContent.substr(0, offset) + elm.textContent + node.textContent.substr(offset);
			setCaret(node, offset + elm.textContent.length);
		} else {
			// With HTML element
			const next = node.splitText(offset);

			node.parentNode.insertBefore(elm, next);
			setCaret(next, 0);
		}
	}
}

/**
 * Expands the function pill based on the number of arguments
 *  `[if]` becomes `[if(]` `[;]` `[)]`
 *
 * Example:
 *  ```
 *  Node:
 *  <imt-pill class="function first" arguments="2">get</imt-pill>,
 *
 *  Result:
 *  [
 *    <imt-pill class="function first" arguments="2">get(</imt-pill>,
 *    <imt-pill class="function separator">;</imt-pill>,
 *    <imt-pill class="function last">)</imt-pill>,
 *   ]
 *  ```
 *
 * @param {HTMLElement} node
 * @returns {HTMLElement[]}
 */

export function completeFunctionSignature(functionPill) {
	const nodes = [functionPill];
	const argumentCount = parseInt(functionPill.getAttribute('arguments') || 0);

	functionPill.textContent = `${functionPill.getAttribute('name')}`;

	for (let i = 1; i < argumentCount; i += 1) {
		nodes.push(create('imt-pill.function.separator ;'));
	}

	nodes.push(create('imt-pill.function.last )'));

	return nodes;
}

export function isCoderContainer(node) {
	return (node?.nodeName === 'IMT-CODER-TOKEN' || isAiBlock(node)) ?? false;
}

/**
 * Get text from clipboard in a plain format
 * @param event {ClipboardEvent}
 * @returns {string}
 */
export function getTextFromClipboard(event) {
	if (window.clipboardData) {
		window.clipboardData.getData('Text'); // ie
	}

	return event.clipboardData.getData('text/plain'); // browsers
}

/**
 * Set text to clipboard in a plain format
 * @param text {string}
 * @param event {ClipboardEvent}
 */
export function setTextToClipboard(text, event) {
	if (window.clipboardData) {
		window.clipboardData.setData('Text', text); // ie
		return;
	}

	event.clipboardData.setData('text/plain', text); // browsers
}

/**
 * Resolves the icon source URL for a coder pill group.
 * Priority: group icon override → module-specific icon (behind FF) → SDK app icon → package icon.
 *
 * @param {object} group - Coder pill group with pills, icon, and metadata
 * @returns {string|undefined} Icon source URL
 */
export function getGroupIconSrc(group) {
	if (!group) {
		return group;
	}

	const referenceNode = group?.pills?.[0]?.node;

	// If the icon is set directly on Group as override, propagate it directly without any source replacing
	if (group.icon && typeof group.icon === 'string') {
		// This is purposefully group and not referenceNode, because it was interfering with Surface Icon Links
		return group.icon;
	}

	const packageName = referenceNode?.package?.name;
	const moduleName = referenceNode?.config?.name;

	if (packageName && moduleName && Features.isOn('module-specific-icons')) {
		return getPackageIconSource(packageName, 32, moduleName);
	}

	if (/^app#/.test(packageName)) {
		return IML.execute(IML.parse(FormanConfig.config.common.icons.sdkPath), {
			name: packageName.replace(/^app#/, ''),
		});
	}

	return IML.execute(IML.parse(FormanConfig.config.common.icons.path), { name: packageName });
}

/**
 * Returns the caret's offset within an element child, or null if the caret is not inside it.
 * Text nodes contribute their character count, element nodes (pills, etc.) contribute 1 unit each.
 * Editable containers (IMT-CODER-TOKEN, AI blocks) contribute their full text length; other elements contribute 1.
 */
function getElementCaretOffset(child, caret) {
	if (isCoderContainer(child)) {
		if (!child.contains(caret.node)) return null;
		return getOffsetWithinContainer(child, caret.node, caret.offset);
	}
	return child.contains(caret.node) ? 0 : null;
}

/** Returns how many characters an element child contributes to the flat offset. */
function getElementCharLength(child) {
	return isCoderContainer(child) ? child.textContent.length : 1;
}

export function getCaretFlatOffset(coderElement) {
	const caret = getCaret();
	if (!caret.node) return -1;

	// Verify the caret is actually inside this coder element
	if (!coderElement.contains(caret.node)) return -1;

	let flatOffset = 0;

	for (const child of coderElement.childNodes) {
		if (child === caret.node) return flatOffset + caret.offset;

		if (child.nodeType === 3) {
			flatOffset += child.textContent.length;
		} else if (child.nodeType === 1) {
			const offset = getElementCaretOffset(child, caret);
			if (offset !== null) return flatOffset + offset;
			flatOffset += getElementCharLength(child);
		}
	}

	return -1;
}

/**
 * Calculates the flat offset within an editable container element.
 * @param {HTMLElement} container - The container element
 * @param {Node} targetNode - The node where the caret is
 * @param {number} targetOffset - The offset within the target node
 * @returns {number}
 */
function getOffsetWithinContainer(container, targetNode, targetOffset) {
	let offset = 0;

	function traverse(node) {
		if (node === targetNode) {
			offset += targetOffset;
			return true;
		}

		if (node.nodeType === 3) {
			offset += node.textContent.length;
		} else if (node.nodeType === 1) {
			for (const child of node.childNodes) {
				if (traverse(child)) return true;
			}
		}
		return false;
	}

	for (const child of container.childNodes) {
		if (traverse(child)) break;
	}

	return offset;
}

/**
 * Finds the text node and offset within an editable container for a given flat offset.
 * @param {HTMLElement} container - The container element
 * @param {number} targetOffset - The flat offset to find
 * @returns {{node: Text, offset: number} | null}
 */
function findPositionInContainer(container, targetOffset) {
	let remaining = targetOffset;

	function traverse(node) {
		if (node.nodeType === 3) {
			if (remaining <= node.textContent.length) {
				return { node, offset: remaining };
			}
			remaining -= node.textContent.length;
		} else if (node.nodeType === 1) {
			for (const child of node.childNodes) {
				const result = traverse(child);
				if (result) return result;
			}
		}
		return null;
	}

	for (const child of container.childNodes) {
		const result = traverse(child);
		if (result) return result;
	}

	// Fallback: find last text node
	const lastTextNode = findLastTextNodeIn(container);
	if (lastTextNode) {
		return { node: lastTextNode, offset: lastTextNode.textContent.length };
	}
	return null;
}

/**
 * Finds the last text node inside an element.
 * @param {HTMLElement} element
 * @returns {Text|null}
 */
function findLastTextNodeIn(element) {
	for (let i = element.childNodes.length - 1; i >= 0; i--) {
		const child = element.childNodes[i];
		if (child.nodeType === 3) return child;
		if (child.nodeType === 1) {
			const found = findLastTextNodeIn(child);
			if (found) return found;
		}
	}
	return null;
}

/**
 * Restores the caret from a flat offset within the coder element.
 *
 * @param {HTMLElement} coderElement - The <imt-coder> element
 * @param {number} flatOffset - The flat offset to restore to
 * Try to set caret in a text node. Returns remaining offset if caret wasn't set here.
 * @returns {number|null} Remaining offset, or null if caret was set
 */
function trySetCaretInTextNode(node, remaining) {
	if (remaining <= node.textContent.length) {
		setCaret(node, remaining);
		return null;
	}
	return remaining - node.textContent.length;
}

/**
 * Try to set caret in an editable container element. Returns remaining offset if caret wasn't set here.
 * @returns {number|null} Remaining offset, or null if caret was set
 */
function trySetCaretInContainer(element, remaining) {
	const containerLength = element.textContent.length;
	if (remaining <= containerLength) {
		const position = findPositionInContainer(element, remaining);
		if (position) {
			setCaret(position.node, position.offset);
		}
		return null;
	}
	return remaining - containerLength;
}

/**
 * Try to set caret at a non-editable element (like a pill). Returns remaining offset if caret wasn't set here.
 * @returns {number|null} Remaining offset, or null if caret was set
 */
function trySetCaretAtElement(element, remaining) {
	if (remaining === 0) {
		// Position caret in adjacent text node
		const prev = element.previousSibling;
		const next = element.nextSibling;
		if (prev?.nodeType === 3) {
			setCaret(prev, prev.textContent.length);
		} else if (next?.nodeType === 3) {
			setCaret(next, 0);
		}
		return null;
	}
	return remaining - 1;
}

export function setCaretFromFlatOffset(coderElement, flatOffset) {
	if (flatOffset < 0 || !coderElement.childNodes.length) {
		return;
	}

	let remaining = flatOffset;

	for (const child of coderElement.childNodes) {
		const isTextNode = child.nodeType === 3;
		const isElementNode = child.nodeType === 1;

		if (isTextNode) {
			remaining = trySetCaretInTextNode(child, remaining);
		} else if (isElementNode) {
			remaining = isCoderContainer(child)
				? trySetCaretInContainer(child, remaining)
				: trySetCaretAtElement(child, remaining);
		}

		if (remaining === null) {
			return;
		}
	}

	// Fallback: position at end of last text node
	const lastChild = coderElement.lastChild;
	if (lastChild?.nodeType === 3) {
		setCaret(lastChild, lastChild.textContent.length);
	}
}
